/**
 * Morgan response-config Studio.
 *
 * Warren-facing Prompt Studio that finishes the response system: he
 * can tune persona/tone/length/formatting/citations/next-steps,
 * preview the effect against a sample question, view the full audit
 * history, and roll back to any earlier version without a redeploy.
 *
 * Publish endpoints (feedback → prompt, feedback → KB) live on the
 * review page — this page owns the base config only.
 */
import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import { Loader2, Save, RotateCcw, Eye, History, Volume2, Play } from "lucide-react";
import { toast } from "sonner";


const LENGTHS = [
  { v: "automatic", label: "Automatic — pick per question" },
  { v: "concise",   label: "Concise — 1–2 sentences" },
  { v: "standard",  label: "Standard — short paragraph or bullets" },
  { v: "detailed",  label: "Detailed — sections, steps, tables" },
];
const FORMATTING = [
  { v: "markdown",   label: "Markdown (default)" },
  { v: "plain",      label: "Plain text" },
  { v: "structured", label: "Structured (headings + bullets)" },
];
const TONES = [
  { v: "professional",     label: "Professional" },
  { v: "friendly",         label: "Friendly" },
  { v: "direct",           label: "Direct" },
  { v: "coach",            label: "Coach" },
  { v: "concise-analyst",  label: "Concise analyst" },
];


export default function AdminMorganStudio() {
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [rollingBack, setRollingBack] = useState(null);
  const [cfg, setCfg]               = useState(null);
  const [voiceModels, setVoiceModels] = useState(["eleven_multilingual_v2", "eleven_turbo_v2_5", "eleven_flash_v2_5"]);
  const [voiceSample, setVoiceSample] = useState("Morgan speaking. This is a live sample of your current voice settings.");
  const [voiceSamplePlaying, setVoiceSamplePlaying] = useState(false);
  const [meta, setMeta]             = useState({});
  const [note, setNote]             = useState("");
  const [previewQ, setPreviewQ]     = useState("How do I plan a Whole Foods launch?");
  const [previewOut, setPreviewOut] = useState(null);
  const [versions, setVersions]     = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/morgan/response-config");
      setCfg(r.data.config);
      if (Array.isArray(r.data?.defaults?.voice_models) && r.data.defaults.voice_models.length) {
        setVoiceModels(r.data.defaults.voice_models);
      }
      setMeta({ v: r.data.active_version, at: r.data.updated_at, by: r.data.updated_by });
    } finally { setLoading(false); }
  };
  const loadVersions = async () => {
    const r = await api.get("/admin/morgan/response-config/versions");
    setVersions(r.data.versions || []);
  };
  useEffect(() => { load(); loadVersions(); }, []);

  const patch = (k, v) => setCfg((prev) => ({ ...prev, [k]: v }));
  // Deep-patch the `voice` sub-block so a slider tweak doesn't clear
  // sibling knobs. Save endpoint expects a partial `voice` object.
  const patchVoice = (k, v) => setCfg((prev) => ({
    ...prev, voice: { ...(prev?.voice || {}), [k]: v },
  }));

  const playVoiceSample = async () => {
    if (voiceSamplePlaying) return;
    setVoiceSamplePlaying(true);
    try {
      const r = await api.post(
        "/account/assistant/voice/tts",
        { text: voiceSample.slice(0, 800), assistant_message_id: null },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.onended = () => { setVoiceSamplePlaying(false); URL.revokeObjectURL(url); };
      audio.onerror = () => { setVoiceSamplePlaying(false); toast.error("Voice sample playback failed"); };
      await audio.play();
    } catch (e) {
      setVoiceSamplePlaying(false);
      toast.error(e?.response?.data?.detail || "Voice sample failed — save first, then retry");
    }
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/morgan/response-config", {
        ...cfg, note: note.trim() || null,
      });
      setMeta({ v: r.data.active_version, at: r.data.updated_at, by: r.data.updated_by });
      setNote("");
      toast.success("Morgan config saved");
      loadVersions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const preview = async () => {
    if (!previewQ.trim() || !cfg) return;
    try {
      const r = await api.post("/admin/morgan/response-config/preview", {
        question: previewQ,
        draft: cfg,
      });
      setPreviewOut(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Rollback to this version? A new version row is created; nothing is deleted.")) return;
    setRollingBack(versionId);
    try {
      const r = await api.post(`/admin/morgan/response-config/rollback/${versionId}`);
      setCfg(r.data.config);
      setMeta({ v: r.data.active_version, at: r.data.updated_at, by: r.data.updated_by });
      toast.success("Rolled back — new version " + r.data.active_version.slice(0, 8));
      loadVersions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    } finally { setRollingBack(null); }
  };

  return (
    <AppShell>
      <div className="w-full py-6">
        <PageHero
          eyebrow="Morgan · Prompt Studio"
          title="Response controls"
          subtitle="Tune Morgan's persona, tone, length, formatting, citation policy, and next-step behavior. Every save is versioned and reversible."
        />

        {loading || !cfg ? (
          <div className="mt-8 text-center text-sm text-neutral-500">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="mt-4 mb-6 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs text-neutral-600 flex items-center gap-4" data-testid="morgan-active-version">
              <span><b>Active version:</b> {meta.v ? meta.v.slice(0, 12) : "unsaved"}</span>
              <span><b>Edited by:</b> {meta.by || "—"}</span>
              <span><b>At:</b> {meta.at ? new Date(meta.at).toLocaleString() : "—"}</span>
            </div>

            <Card className="p-5 space-y-5" data-testid="morgan-studio-form">
              <Field label="Persona (system-prompt override)"
                     hint="Warren-authored — becomes Morgan's identity + boundaries. Leave blank to use the platform default.">
                <Textarea value={cfg.persona || ""} onChange={(e) => patch("persona", e.target.value)}
                          rows={6} className="font-mono text-xs" data-testid="morgan-persona" />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Tone">
                  <Select value={cfg.tone} onValueChange={(v) => patch("tone", v)}>
                    <SelectTrigger data-testid="morgan-tone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TONES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Response length">
                  <Select value={cfg.response_length} onValueChange={(v) => patch("response_length", v)}>
                    <SelectTrigger data-testid="morgan-length"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LENGTHS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Formatting style">
                  <Select value={cfg.formatting_style} onValueChange={(v) => patch("formatting_style", v)}>
                    <SelectTrigger data-testid="morgan-formatting"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FORMATTING.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Model">
                  <Input value={cfg.model || ""} onChange={(e) => patch("model", e.target.value)}
                         placeholder="e.g. claude-sonnet-4-5-20250929" data-testid="morgan-model" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ToggleField label="Require citations on material factual claims"
                             checked={!!cfg.citation_required}
                             onChange={(v) => patch("citation_required", v)}
                             testid="morgan-citations" />
                <ToggleField label="Suggest a single next step when supported"
                             checked={!!cfg.include_next_steps}
                             onChange={(v) => patch("include_next_steps", v)}
                             testid="morgan-nextsteps" />
              </div>

              <Field label="Refusal / insufficient-evidence behavior"
                     hint="Exact wording Morgan should use when the retrieved sources don't fully answer.">
                <Textarea value={cfg.refusal_behavior || ""} onChange={(e) => patch("refusal_behavior", e.target.value)}
                          rows={3} data-testid="morgan-refusal" />
              </Field>

              {/* ────────────── Voice (ElevenLabs TTS) ─────────────── */}
              <div className="pt-4 mt-2 border-t border-neutral-200" data-testid="morgan-voice-section">
                <div className="flex items-center gap-2 mb-1">
                  <Volume2 className="h-4 w-4 text-emerald-600" />
                  <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-700">Voice · ElevenLabs</Label>
                </div>
                <p className="text-[11px] text-neutral-500 mb-3">
                  Warren-tuned voice output. Empty voice ID falls back to the platform default. All knobs are applied on the next TTS play — no restart required.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ToggleField label="Voice output enabled"
                               checked={!!cfg.voice?.enabled}
                               onChange={(v) => patchVoice("enabled", v)}
                               testid="morgan-voice-enabled" />
                  <ToggleField label="Speaker boost"
                               checked={!!cfg.voice?.speaker_boost}
                               onChange={(v) => patchVoice("speaker_boost", v)}
                               testid="morgan-voice-speaker-boost" />
                  <Field label="Voice ID" hint="ElevenLabs voice_id. Blank = platform default.">
                    <Input value={cfg.voice?.voice_id || ""}
                           onChange={(e) => patchVoice("voice_id", e.target.value)}
                           placeholder="e.g. 21m00Tcm4TlvDq8ikWAM"
                           data-testid="morgan-voice-id" />
                  </Field>
                  <Field label="Model">
                    <Select value={cfg.voice?.model_id || "eleven_multilingual_v2"}
                            onValueChange={(v) => patchVoice("model_id", v)}>
                      <SelectTrigger data-testid="morgan-voice-model"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {voiceModels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Slider01 label="Stability"
                            hint="Lower = more expressive, higher = more consistent."
                            value={cfg.voice?.stability ?? 0.5}
                            onChange={(v) => patchVoice("stability", v)}
                            testid="morgan-voice-stability" />
                  <Slider01 label="Similarity"
                            hint="Match to the reference voice."
                            value={cfg.voice?.similarity ?? 0.75}
                            onChange={(v) => patchVoice("similarity", v)}
                            testid="morgan-voice-similarity" />
                  <Slider01 label="Style"
                            hint="Higher = follow style patterns of the reference. Costs latency."
                            value={cfg.voice?.style ?? 0.0}
                            onChange={(v) => patchVoice("style", v)}
                            testid="morgan-voice-style" />
                </div>
                <div className="mt-3 flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-neutral-500">Sample text</Label>
                    <Input value={voiceSample} onChange={(e) => setVoiceSample(e.target.value)}
                           data-testid="morgan-voice-sample-text" />
                  </div>
                  <Button variant="outline" onClick={playVoiceSample} disabled={voiceSamplePlaying}
                          data-testid="morgan-voice-play-sample">
                    {voiceSamplePlaying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
                    Play sample
                  </Button>
                </div>
                <p className="text-[10px] text-neutral-400 mt-1">
                  Sample plays with the currently <b>saved</b> config. Save first to hear the effect of unsaved edits.
                </p>
              </div>

              <Field label="Changelog note (optional)">
                <Input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="What changed and why?" data-testid="morgan-note" />
              </Field>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-neutral-200">
                <Button variant="outline" onClick={() => setShowHistory((v) => !v)} data-testid="morgan-toggle-history">
                  <History className="h-4 w-4 mr-1.5" /> {showHistory ? "Hide" : "Show"} history
                </Button>
                <Button variant="outline" onClick={preview} data-testid="morgan-preview-btn">
                  <Eye className="h-4 w-4 mr-1.5" /> Preview
                </Button>
                <Button onClick={save} disabled={saving} data-testid="morgan-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save new version
                </Button>
              </div>
            </Card>

            {/* Preview panel */}
            <Card className="mt-4 p-4">
              <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-600">
                Preview against a sample question
              </Label>
              <div className="mt-2 flex gap-2">
                <Input value={previewQ} onChange={(e) => setPreviewQ(e.target.value)}
                       placeholder="Try: 'What is COGS?' or 'Walk me through pricing for Whole Foods'"
                       data-testid="morgan-preview-q" />
                <Button variant="outline" onClick={preview} data-testid="morgan-preview-run">Render</Button>
              </div>
              {previewOut && (
                <div className="mt-3 text-xs" data-testid="morgan-preview-output">
                  <div className="text-neutral-500 mb-1">
                    Resolved depth: <b className="text-neutral-800">{previewOut.resolved_depth}</b>
                  </div>
                  <pre className="whitespace-pre-wrap bg-neutral-50 border border-neutral-200 rounded-md p-3 max-h-72 overflow-auto font-mono text-[11px]">
                    {previewOut.system_prompt}
                  </pre>
                </div>
              )}
            </Card>

            {/* Version history */}
            {showHistory && (
              <Card className="mt-4 p-4" data-testid="morgan-history">
                <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-600">
                  Version history — newest first
                </Label>
                <ul className="mt-3 space-y-2 max-h-96 overflow-auto">
                  {versions.length === 0 && (
                    <li className="text-xs text-neutral-500">No saves yet.</li>
                  )}
                  {versions.map((v) => (
                    <li key={v.id} className="flex items-start justify-between gap-3 border border-neutral-200 rounded-md p-2 text-xs">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] text-neutral-500">{v.id.slice(0, 12)}</div>
                        <div className="text-neutral-700">
                          <b>{v.created_by}</b> · {new Date(v.created_at).toLocaleString()}
                        </div>
                        {v.note && <div className="text-neutral-500 italic mt-0.5 truncate">"{v.note}"</div>}
                        <div className="mt-1 text-[10px] text-neutral-500">
                          {v.config?.tone} · {v.config?.response_length} · {v.config?.formatting_style}
                          {v.config?.citation_required ? " · cite" : ""}
                          {v.config?.include_next_steps ? " · next-step" : ""}
                        </div>
                      </div>
                      <Button size="sm" variant="outline" disabled={rollingBack === v.id || v.id === meta.v}
                              onClick={() => rollback(v.id)} data-testid={`morgan-rollback-${v.id}`}>
                        {rollingBack === v.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><RotateCcw className="h-3 w-3 mr-1" /> Roll back</>}
                      </Button>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}


function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-600">
        {label}
      </Label>
      {hint && <p className="text-[11px] text-neutral-500 mt-0.5 mb-1.5">{hint}</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}


function ToggleField({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2.5">
      <span className="text-sm text-neutral-800">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}


// Compact 0-1 slider with a numeric readout. Range enforced client-
// side but the backend also clamps in `normalize()` so a browser
// devtools tweak can't push a knob out of bounds.
function Slider01({ label, hint, value, onChange, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-600">{label}</Label>
        <span className="text-[11px] font-mono text-neutral-500" data-testid={`${testid}-readout`}>
          {(Number(value) || 0).toFixed(2)}
        </span>
      </div>
      {hint && <p className="text-[11px] text-neutral-500 mt-0.5">{hint}</p>}
      <input
        type="range" min={0} max={1} step={0.05}
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
        className="mt-1 w-full h-2 rounded-lg appearance-none bg-neutral-200 accent-emerald-600"
      />
    </div>
  );
}
